import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import {
  ACQUISITION_SOURCE_OPTIONS,
  lookupCustomersByPhone,
  type AcquisitionSource,
  type CustomerDetail,
} from '@/lib/api/customers.js';
import { CustomerSearchSelect } from '@/components/customers/CustomerSearchSelect.js';

export interface CustomerFormValues extends Record<string, unknown> {
  firstName: string;
  lastName?: string | undefined;
  phone?: string | undefined;
  email?: string | undefined;
  notes?: string | undefined;
  acquisitionSource?: AcquisitionSource | null | undefined;
  acquisitionSourceDetail?: string | null | undefined;
  referredByCustomerId?: string | null | undefined;
  referredByCustomerName?: string | null | undefined;
}

export function CustomerForm({
  initial,
  submitLabel,
  loading,
  excludeCustomerId,
  onSubmit,
}: {
  initial?: Partial<CustomerDetail>;
  submitLabel: string;
  loading?: boolean;
  /** When editing, prevent selecting this customer as their own referrer. */
  excludeCustomerId?: string | null;
  onSubmit: (values: CustomerFormValues) => void;
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CustomerFormValues>({
    defaultValues: {
      firstName: initial?.firstName ?? '',
      lastName: initial?.lastName ?? '',
      phone: initial?.phone ?? '',
      email: initial?.email ?? '',
      notes: initial?.notes ?? '',
      acquisitionSource: initial?.acquisitionSource ?? undefined,
      acquisitionSourceDetail: initial?.acquisitionSourceDetail ?? '',
      referredByCustomerId: initial?.referredByCustomerId ?? null,
      referredByCustomerName: initial?.referredByCustomerName ?? null,
    },
  });

  const acquisitionSource = watch('acquisitionSource');
  const referredByCustomerId = watch('referredByCustomerId');
  const referredByCustomerName = watch('referredByCustomerName');
  const phoneValue = watch('phone');
  const [debouncedPhone, setDebouncedPhone] = useState('');
  const [phoneAcked, setPhoneAcked] = useState(false);
  const showOtherDetail = acquisitionSource === 'other';
  const showReferralPicker = acquisitionSource === 'referral';

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedPhone((phoneValue ?? '').trim()), 400);
    return () => window.clearTimeout(t);
  }, [phoneValue]);

  useEffect(() => {
    setPhoneAcked(false);
  }, [debouncedPhone]);

  const sharedPhoneQuery = useQuery({
    queryKey: ['customer-phone-check', debouncedPhone, excludeCustomerId],
    queryFn: () => lookupCustomersByPhone(debouncedPhone, excludeCustomerId ?? undefined),
    enabled: debouncedPhone.replace(/\D/g, '').length >= 7,
  });

  const sharedPeers = (sharedPhoneQuery.data ?? []).filter((c) => c.id !== excludeCustomerId);
  const needsPhoneAck = sharedPeers.length > 0 && !phoneAcked;

  const submit = handleSubmit((values) => {
    if (needsPhoneAck) return;
    const isReferral = values.acquisitionSource === 'referral';
    onSubmit({
      firstName: values.firstName,
      lastName: values.lastName || undefined,
      phone: values.phone || undefined,
      email: values.email || undefined,
      notes: values.notes || undefined,
      acquisitionSource: values.acquisitionSource || null,
      acquisitionSourceDetail: isReferral
        ? values.referredByCustomerName || null
        : values.acquisitionSourceDetail || null,
      referredByCustomerId: isReferral ? values.referredByCustomerId || null : null,
    });
  });

  return (
    <form className="space-y-4" onSubmit={submit}>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">First name</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
            {...register('firstName', { required: 'First name is required' })}
          />
          {errors.firstName ? (
            <span className="mt-1 block text-xs font-semibold text-red-600">
              {errors.firstName.message}
            </span>
          ) : null}
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Last name</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
            {...register('lastName')}
          />
        </label>
      </div>
      <label className="block">
        <span className="text-sm font-medium text-slate-700">Phone</span>
        <input
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
          placeholder="5551234567"
          {...register('phone', {
            validate: (value, formValues) =>
              Boolean(value || formValues.email) || 'Either phone or email is required',
          })}
        />
        {errors.phone ? (
          <span className="mt-1 block text-xs font-semibold text-red-600">{errors.phone.message}</span>
        ) : null}
      </label>
      {sharedPeers.length > 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          <p className="font-semibold">Another customer has this number:</p>
          <ul className="mt-1 list-inside list-disc">
            {sharedPeers.slice(0, 5).map((c) => (
              <li key={c.id}>
                {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                {c.totalTickets ? ` · ${c.totalTickets} tickets` : ''}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs">Shared numbers are normal for families. Continue anyway?</p>
          <button
            type="button"
            className="mt-2 rounded-lg bg-amber-900 px-3 py-1.5 text-xs font-semibold text-white"
            onClick={() => setPhoneAcked(true)}
          >
            Continue anyway
          </button>
          {phoneAcked ? <p className="mt-1 text-xs text-emerald-800">Confirmed — you can save.</p> : null}
        </div>
      ) : null}
      <label className="block">
        <span className="text-sm font-medium text-slate-700">Email</span>
        <input
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
          type="email"
          placeholder="customer@example.com"
          {...register('email')}
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-slate-700">How did you hear about us?</span>
        <select
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
          {...register('acquisitionSource', {
            onChange: (e) => {
              const next = e.target.value as AcquisitionSource | '';
              if (next !== 'referral') {
                setValue('referredByCustomerId', null);
                setValue('referredByCustomerName', null);
              }
            },
          })}
        >
          <option value="">Select…</option>
          {ACQUISITION_SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      {showOtherDetail ? (
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Please specify</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
            {...register('acquisitionSourceDetail')}
          />
        </label>
      ) : null}
      {showReferralPicker ? (
        <div>
          <span className="text-sm font-medium text-slate-700">Who referred them?</span>
          <div className="mt-1">
            <CustomerSearchSelect
              selectedId={referredByCustomerId}
              selectedName={referredByCustomerName}
              excludeCustomerId={excludeCustomerId}
              onSelect={(c) => {
                setValue('referredByCustomerId', c.id);
                setValue('referredByCustomerName', c.name);
              }}
              onClear={() => {
                setValue('referredByCustomerId', null);
                setValue('referredByCustomerName', null);
              }}
            />
          </div>
        </div>
      ) : null}
      <label className="block">
        <span className="text-sm font-medium text-slate-700">Notes</span>
        <textarea
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
          rows={3}
          {...register('notes')}
        />
      </label>
      <p className="text-xs text-slate-500">Either phone or email is required.</p>
      <button
        type="submit"
        disabled={loading || needsPhoneAck}
        className="w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-50"
      >
        {loading ? 'Saving…' : submitLabel}
      </button>
    </form>
  );
}
